import React from "react"
import { Table, Button, Typography } from "antd"
import { PlusOutlined } from "@ant-design/icons"
import { useTranslation } from "react-i18next"
import { NewConfirmModal } from "ui/NewConfirmModal"
import AssignExperiencesModal from "ui/AssignExperiencesModal"
import AddOnModal from "./modal/AddOnModal"
import { useAddOns } from "./hooks/useAddOns"
import { getColumns } from "./columns"
import useActiveGuide from "hooks/useActiveClient"
import styles from "../TaxesAndFees/index.module.css"

const { Title, Text } = Typography

const CONFIRM_COPY = {
  delete: { titleKey: "settings.add_ons.delete_title", messageKey: "settings.add_ons.delete_message", confirmKey: "settings.action_delete", titleDefault: "Delete Add-on?", messageDefault: "Are you sure you want to delete:", confirmDefault: "Delete" },
  deactivate: { titleKey: "settings.add_ons.deactivate_title", messageKey: "settings.add_ons.deactivate_message", confirmKey: "settings.action_deactivate", titleDefault: "Deactivate Add-on?", messageDefault: "Are you sure you want to deactivate:", confirmDefault: "Deactivate" },
  activate: { titleKey: "settings.add_ons.activate_title", messageKey: "settings.add_ons.activate_message", confirmKey: "settings.action_activate", titleDefault: "Activate Add-on?", messageDefault: "Are you sure you want to activate:", confirmDefault: "Activate" }
}

export const AddOnsSettings = () => {
  const { t } = useTranslation()
  const { isActive: active, isPending } = useActiveGuide()

  const {
    addOns,
    addOnsLoading,
    isCreating,
    isUpdating,
    isDeleting,
    isMutating,
    isModalOpen,
    addOnToEdit,
    handleAddAddOn,
    openAddModal,
    closeModal,
    getMenuItems,
    confirmAction,
    closeConfirm,
    handleConfirm,
    assignExpAddOn,
    openAssignExperiences,
    closeAssignExperiences,
    handleAssignExperiences
  } = useAddOns({ enabled: true })

  const copy = confirmAction ? CONFIRM_COPY[confirmAction.type] : null

  return (
    <>
      <div className={styles.container}>
        <div className={styles.headerRow}>
          <div className={styles.titleWithInfo}>
            <Title level={4}>{t("settings.add_ons.section_title", { defaultValue: "Add-ons" })}</Title>
          </div>
          <Button
            color="primary"
            variant="solid"
            icon={<PlusOutlined />}
            onClick={active && !isPending ? openAddModal : undefined}
            disabled={!active || isPending}
            aria-disabled={!active || isPending}
          >
            {t("settings.add_new")}
          </Button>
        </div>
        <Text className={styles.description}>
          {t("settings.add_ons.description", { defaultValue: "Create add-ons and assign them to one or more experiences. Guests can pick them during booking." })}
        </Text>
        <div className={styles.tableWrapper}>
          <Table
            columns={getColumns(getMenuItems, isMutating, t, openAssignExperiences)}
            dataSource={addOnsLoading ? undefined : addOns}
            rowKey="id"
            loading={addOnsLoading}
            pagination={false}
            locale={addOnsLoading ? { emptyText: <></> } : undefined}
          />
        </div>
      </div>

      <AddOnModal
        visible={isModalOpen}
        onCancel={closeModal}
        onSave={handleAddAddOn}
        initialValues={addOnToEdit}
        isSaving={isCreating || isUpdating}
      />
      <AssignExperiencesModal
        open={!!assignExpAddOn}
        onCancel={closeAssignExperiences}
        onSave={handleAssignExperiences}
        currentExperiences={assignExpAddOn?.associated_trips || []}
        loading={isUpdating}
      />
      <NewConfirmModal
        visible={!!confirmAction}
        onClose={closeConfirm}
        onConfirm={handleConfirm}
        title={copy ? t(copy.titleKey, { defaultValue: copy.titleDefault }) : ""}
        message={copy ? t(copy.messageKey, { defaultValue: copy.messageDefault }) : ""}
        highlightText={confirmAction?.addOn?.title}
        confirmLabel={copy ? t(copy.confirmKey, { defaultValue: copy.confirmDefault }) : ""}
        loading={isDeleting || isUpdating}
      />
    </>
  )
}

export default AddOnsSettings
